import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';

import ConditionDisplay from './condition_display';
import PriceWithShipping from './price_with_shipping';
import SellerInfo from './seller_info';
import AffirmEstimateListingCardCTA from './affirm_estimate_listing_card_cta';
import CoreLink from './core_link';
import {
  Listing,
  core_apimessages_Listing_ListingType,
} from '../rql';
import AddToCartButton from './add_to_cart_button';
import CreateOfferButton from '../offers/create_offer_button';
import { DisplayStyle, WatchBadge } from './watch_badge';
import {
  withUserContext,
  IUserContext,
} from './user_context_provider';
import { listingItemPagePath } from '../url_helpers';
import { buildListingClickEvent } from '../elog/mparticle_tracker';
import { shouldShowBumpTag } from './bump_tag';
import { WEB } from '../constants';
import { ListingOfferMetadata } from './listing_offer_metadata';
import DynamicImage from './dynamic_image';
import { useUser } from '../user_hooks';
import { RCTextWithIcon } from '../rc';
import BUMP_TAG_SVG from '../../design-system/icons/core/bump-icon.svg?component';

const DIGITAL_LISTING_UUID = 'e71b6e43-7217-4746-b4e3-64bf708a2664';

interface IProps extends IUserContext {
  listing?: Listing;
  position?: number;
  inMobileApp?: boolean;
  hideMetadataAsOffer?: boolean;
  onOfferSuccess?: Function;
  listingsCount?: number;
  showEstimatedMonthlyPayment?: boolean;
  componentName?: string;
  cspFeaturedListingId?: string;
}

function PlaceholderCard() {
  return (
    <div className="listing-row-card listing-row-card--placeholder">
      <div className="listing-row-card__inner">
        <div className="listing-row-card__main">
          <div className="listing-row-card__info-and-image">
            <div className="listing-row-card__image">
              <div className="listing-row-card__image__inner" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BumpTag({ listing }:IProps) {
  if (!shouldShowBumpTag(listing)) { return null; }
  return (
    <div className="listing-row-card__bump-tag">
      <RCTextWithIcon
        svgComponent={BUMP_TAG_SVG}
        placement="left"
      >
        {I18n.t('commons.bumpsView.header')}
      </RCTextWithIcon>
    </div>
  );
}

export function ListingRowCard(props: IProps) {
  const user = useUser();

  const openNewTab = user && user.deviceName === WEB;
  const { listing } = props;

  if (!props.listing) { return <PlaceholderCard />; }

  const classes = classNames(
    'listing-row-card',
    { 'listing-row-card--software': listing.categoryUuids.includes(DIGITAL_LISTING_UUID) },
    { 'listing-row-card--discounted': !!listing.pricing.originalPrice },
  );

  const clickEvent = buildListingClickEvent({
    listing,
    isFeaturedInCSPCollection: props.cspFeaturedListingId === listing.id,
    position: props.position,
    listingsCount: props.listingsCount,
    componentName: props.componentName,
  });

  return (
    <div className={classes}>
      <div className="listing-row-card__inner">
        {!props.hideMetadataAsOffer && <ListingOfferMetadata listing={listing} />}
        <div className="listing-row-card__main">
          <div className="listing-row-card__image-container">
            <div className="listing-row-card__image">
              <CoreLink
                to={listingItemPagePath(listing)}
                target={openNewTab ? '_blank' : undefined}
                clickEvent={clickEvent}
              >
                <DynamicImage
                  src={listing.images[0].source}
                />
              </CoreLink>
            </div>
            <div className="listing-row-card__shop-info--mobile">
              <SellerInfo listing={listing} hideBadges />
              <BumpTag listing={listing} />
            </div>
          </div>
          <div className="listing-row-card__info-container">
            <div className="listing-row-card__title-and-price">
              <div>
                <CoreLink
                  to={listingItemPagePath(listing)}
                  className="listing-row-card__title"
                  target={openNewTab ? '_blank' : undefined}
                  clickEvent={clickEvent}
                >
                  {listing.title}
                </CoreLink>
                <div className="listing-row-card__condition">
                  <ConditionDisplay
                    slug={listing.condition.conditionSlug}
                    displayName={listing.condition.displayName}
                    textOnly
                  />
                </div>
              </div>
              <div className="listing-row-card__pricing">
                <PriceWithShipping listing={listing} />
                {props.showEstimatedMonthlyPayment &&
                  <div className="mtb-half">
                    <AffirmEstimateListingCardCTA listing={listing} />
                  </div>
                }
              </div>
            </div>
            <div className="listing-row-card__shop-and-actions">
              <div className="listing-row-card__shop-info">
                <SellerInfo listing={listing} hideBadges />
                <BumpTag listing={listing} />
              </div>
              <div className="listing-row-card__pricing__actions">
                <div className="listing-row-card__pricing__action">
                  <WatchBadge
                    listing={listing}
                    position={props.position}
                    parentComponentName={props.componentName}
                    displayStyle={DisplayStyle.TEXT}
                  />
                </div>
                { listing.listingType === core_apimessages_Listing_ListingType.MAKE_AN_OFFER &&
                  <div className="listing-row-card__pricing__action">
                    <CreateOfferButton
                      listingId={listing.id}
                      listing={listing}
                      onOfferSuccess={props.onOfferSuccess}
                      position={props.position}
                      parentComponentName={props.componentName}
                      bumpKey={listing.bumpKey}
                    />
                  </div>
                }
                <div className="listing-row-card__pricing__action">
                  <AddToCartButton
                    listing={listing}
                    position={props.position}
                    parentComponentName={props.componentName}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withUserContext<IProps>(ListingRowCard);
